import { createClient, Session, AuthChangeEvent } from "@supabase/supabase-js";
import { siteConfig } from "../config/site";
import { API_URL } from "../config/env";
import { safeLocalStorage } from "../utils/safeLocalStorage";

// Supabase configuration - use environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
// Legacy anon key (PRIMARY) - new publishable key not yet working with Supabase client
const supabaseAnonKey =
  import.meta.env.VITE_SUPABASE_ANON_KEY ||
  import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
const supabaseAuthUrl = import.meta.env.VITE_SUPABASE_AUTH_URL;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error("Missing required Supabase environment variables");
}

// Extract Supabase project reference from URL for environment-specific storage
// This prevents token contamination between production and staging
const getSupabaseProjectRef = (url: string): string => {
  try {
    const match = url.match(/https:\/\/([^.]+)\.supabase\.co/);
    return match?.[1] || "default";
  } catch {
    return "default";
  }
};
const supabaseProjectRef = getSupabaseProjectRef(supabaseUrl);
const STORAGE_KEY = `vitly-supabase-auth-${supabaseProjectRef}`;

const _getCookie = (key: string): string | null => {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${key}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

const _setCookie = (key: string, value: string) => {
  if (typeof document === "undefined") return;
  const secure = window?.location?.protocol === "https:" ? "; Secure" : "";
  const sameSite = "; SameSite=Lax";
  const path = "; Path=/";
  const maxAge = "; Max-Age=300"; // keep for 5 minutes which exceeds auth flow
  document.cookie = `${key}=${encodeURIComponent(value)}${path}${sameSite}${secure}${maxAge}`;
};

const removeCookie = (key: string) => {
  if (typeof document === "undefined") return;
  document.cookie = `${key}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
};

/**
 * Custom storage adapter for Supabase auth
 *
 * NOTE: The Supabase JavaScript client requires access to tokens for its internal
 * operations (session validation, auto-refresh, etc.). Tokens are stored in localStorage
 * for the Supabase client to function properly.
 *
 * For our BACKEND API calls, we also persist tokens to httpOnly cookies via
 * persistSessionToBackend(). This provides XSS protection for backend requests
 * while maintaining Supabase client functionality.
 *
 * Architecture:
 * - Supabase client operations: Uses tokens from localStorage
 * - Our backend API calls: Uses httpOnly cookies (withCredentials:true)
 */
const customStorage = {
  getItem: (key: string) => {
    const item = safeLocalStorage.getItem(key);
    if (import.meta.env.DEV) {
      console.log(`[Storage] Getting ${key}:`, item ? "found" : "not found");
    }
    return item;
  },
  setItem: (key: string, value: string) => {
    safeLocalStorage.setItem(key, value);
    if (import.meta.env.DEV) {
      console.log(`[Storage] Setting ${key}`);
    }
  },
  removeItem: (key: string) => {
    safeLocalStorage.removeItem(key);
    removeCookie(key);
    if (import.meta.env.DEV) {
      console.log(`[Storage] Removing ${key}`);
    }
  },
};

// Proactively clean up legacy storage keys that could cause token contamination
// This runs BEFORE Supabase client initialization to prevent stale tokens from being used
(function cleanupLegacyTokens() {
  if (typeof window === "undefined") return;

  const currentStorageKey = STORAGE_KEY;
  const legacyKeys = [
    "vitly-supabase-auth", // Old universal key (pre-environment-specific fix)
  ];

  // Also clean up tokens from OTHER Supabase projects (cross-environment contamination)
  const otherProjectKeys = Object.keys(localStorage).filter((key) => {
    // Keep current environment's key, remove everything else related to auth
    if (key === currentStorageKey) return false;
    return (
      key.startsWith("vitly-supabase-auth") ||
      key.startsWith("sb-") ||
      key.includes("supabase")
    );
  });

  const keysToRemove = [...legacyKeys, ...otherProjectKeys];
  let foundStaleTokens = false;

  keysToRemove.forEach((key) => {
    if (localStorage.getItem(key)) {
      console.warn(`[Supabase] Removing stale auth token: ${key}`);
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
      // Also clear cookie version
      document.cookie = `${key}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
      foundStaleTokens = true;
    }
  });

  // Note: Backend httpOnly cookies are NOT cleared here automatically.
  // They are only cleared on explicit logout via clearBackendSession().
  // This prevents redirect loops when localStorage is out of sync with cookies.
  if (foundStaleTokens) {
    console.info("[Supabase] Cleaned up stale localStorage tokens (backend cookies preserved)",
    );
  }
})();

// Create Supabase client with secure configuration
// Note: Custom auth domain is configured in Supabase dashboard, not in client config
export const supabase = createClient(
  // Use custom auth URL as the main URL if provided, otherwise use regular URL
  supabaseAuthUrl || supabaseUrl,
  supabaseAnonKey,
  {
    auth: {
      autoRefreshToken: true, // Enable auto-refresh for Supabase auth
      persistSession: true,
      detectSessionInUrl: true, // Let Supabase resolve OAuth callbacks per official docs
      storage: customStorage,
      storageKey: STORAGE_KEY, // Environment-specific key to prevent token contamination
      flowType: "implicit", // Use implicit flow for improved compatibility when storage is blocked
      debug: false, // Disable debug mode in production
    },
    global: {
      headers: {
        "x-application-name": siteConfig.name.toLowerCase(),
      },
    },
    db: {
      schema: "public",
    },
    realtime: {
      params: {
        eventsPerSecond: 10, // Rate limiting
      },
    },
  },
);

export const persistSessionToBackend = async (
  session: Session | null,
): Promise<void> => {
  if (!session?.access_token) {
    return;
  }

  // Refresh token is optional - we can persist session with just the access token
  // The backend will still set httpOnly cookies for the access token
  const hasRefresh =
    typeof session.refresh_token === "string" &&
    session.refresh_token.length >= 20;

  if (!hasRefresh) {
    console.debug("[Supabase] Persisting session without refresh token");
  }

  const payload = {
    accessToken: session.access_token,
    refreshToken: hasRefresh ? session.refresh_token : undefined,
    expiresAt: session.expires_at ?? undefined,
    expiresIn: session.expires_in ?? undefined,
  };

  try {
    const response = await fetch(`${API_URL}/v1/auth/session`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const message = await response.text().catch(() => response.statusText);
      console.warn("[Supabase] Backend session persistence returned non-200:",
        response.status,
        message,
      );
    }
  } catch (error) {
    console.warn("[Supabase] Failed to persist session cookies with backend:",
      error,
    );
  }
};

export const clearBackendSession = async () => {
  try {
    await fetch(`${API_URL}/v1/auth/session`, {
      method: "DELETE",
      credentials: "include",
    });
  } catch (error) {
    console.warn("[Supabase] Failed to clear backend session cookies:", error);
  }
};

// Helper functions for auth operations
export const signIn = async (email: string, password: string) => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (error) throw error;
  await persistSessionToBackend(data.session);
  return data;
};

export const signUp = async (
  email: string,
  password: string,
  metadata?: Record<string, unknown>,
) => {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: metadata,
      emailRedirectTo: siteConfig.auth.welcomeUrl,
    },
  });

  if (error) throw error;
  return data;
};

export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  await clearBackendSession();
  if (error) throw error;
};

export const resetPassword = async (email: string) => {
  const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: siteConfig.auth.resetPasswordUrl,
  });

  if (error) throw error;
  return data;
};

export const updatePassword = async (newPassword: string) => {
  const { data, error } = await supabase.auth.updateUser({
    password: newPassword,
  });

  if (error) throw error;
  return data;
};

// Import mobile OAuth utilities
import {
  detectMobileEnvironment,
  handleMobileOAuthRedirect,
  handleMobileOAuthError,
  showMobileOAuthInstructions,
} from "../utils/mobileOAuthHandler";

export const signInWithGoogle = async (additionalScopes?: string[]) => {
  // Clear any stale OAuth state before starting new flow (safe for iOS Safari private mode)
  try {
    sessionStorage.removeItem("oauth_state");
    sessionStorage.removeItem("oauth_csrf");
    sessionStorage.removeItem("oauth_timestamp");
  } catch {
    // Ignore sessionStorage errors in private mode
  }
  safeLocalStorage.removeItem("vitly-supabase-auth-code-verifier");

  // Detect mobile environment using enhanced handler
  const mobileEnv = detectMobileEnvironment();

  // Show mobile instructions if needed
  if (mobileEnv.isMobile) {
    const shouldProceed = showMobileOAuthInstructions(mobileEnv);
    if (!shouldProceed) {
      throw new Error("User cancelled mobile OAuth flow");
    }
  }

  // Default scopes with optional additions
  const defaultScopes = ["openid", "email", "profile"];
  const requestedScopes = additionalScopes
    ? [...defaultScopes, ...additionalScopes]
    : defaultScopes;

  // Mobile-optimized OAuth configuration
  const oauthOptions = {
    redirectTo: siteConfig.auth.redirectUrl,
    queryParams: {
      access_type: "offline",
      prompt: "consent",
    } as Record<string, string>,
    scopes: requestedScopes.join(" "),
    // Only skip browser redirect for mobile (where we need custom handling)
    // Desktop browsers should use Supabase's built-in redirect + detectSessionInUrl
    skipBrowserRedirect: mobileEnv.isMobile,
  };

  // No mobile-specific query overrides needed - the base params work for all platforms

  try {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: oauthOptions,
    });

    if (error) {
      const friendlyError = handleMobileOAuthError(error, mobileEnv);
      throw new Error(friendlyError);
    }

    if (!data?.url) {
      throw new Error("No OAuth URL received from Supabase");
    }

    // Handle mobile redirect with enhanced logic (only for mobile)
    if (mobileEnv.isMobile) {
      await handleMobileOAuthRedirect(data.url, mobileEnv);
    }
    // For desktop: Supabase automatically redirects when skipBrowserRedirect=false
    // No manual redirect needed - detectSessionInUrl will handle tokens on callback

    return data;
  } catch (error) {
    // Use mobile-aware error handling
    const friendlyError = handleMobileOAuthError(error, mobileEnv);
    throw new Error(friendlyError);
  }
};

export const getSession = async () => {
  const {
    data: { session },
    error,
  } = await supabase.auth.getSession();
  if (error) throw error;
  return session;
};

export const refreshSession = async () => {
  const {
    data: { session },
    error,
  } = await supabase.auth.refreshSession();
  if (error) throw error;
  return session;
};

// Subscribe to auth state changes
export const onAuthStateChange = (
  callback: (event: AuthChangeEvent, session: Session | null) => void,
) => {
  return supabase.auth.onAuthStateChange(callback);
};

// Get current user
export const getCurrentUser = async () => {
  const {
    data: { user },
    error,
  } = await supabase.auth.getUser();
  if (error) throw error;
  return user;
};

// Update user profile
export const updateProfile = async (updates: Record<string, unknown>) => {
  const { data, error } = await supabase.auth.updateUser({
    data: updates,
  });

  if (error) throw error;
  return data;
};

export default supabase;
